import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import '../styles/LandingPage.css';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Find Trusted <span className="highlight">Home Helpers</span> Near You
            </h1>
            <p className="hero-subtitle">
              Book reliable house helpers for sweeping & mopping, cooking, gardening, and more. 
              Available on-demand or scheduled appointments.
            </p>
            
            {isAuthenticated ? (
              <Link to="/dashboard" className="cta-button">
                Go to Dashboard
              </Link>
            ) : (
              <div className="hero-buttons">
                <Link to="/register" className="cta-button primary">
                  Get Started
                </Link>
                <Link to="/login" className="cta-button secondary">
                  Login
                </Link>
              </div>
            )}
          </div>
          
          <div className="hero-image">
            <div className="hero-placeholder">
              <div className="service-icons">
                <div className="service-icon">🧹</div>
                <div className="service-icon">👨‍🍳</div>
                <div className="service-icon">🌱</div>
                <div className="service-icon">🧺</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services">
        <div className="container">
          <h2 className="section-title">Our Services</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon-large">🧹</div>
              <h3>Sweeping & Mopping</h3>
              <p>Professional sweeping and mopping services for your home</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon-large">👨‍🍳</div>
              <h3>Cooking</h3>
              <p>Delicious home-cooked meals prepared by experts</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon-large">🧺</div>
              <h3>Laundry</h3>
              <p>Complete laundry and ironing services</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon-large">🌱</div>
              <h3>Gardening</h3>
              <p>Garden maintenance and plant care</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon-large">🐕</div>
              <h3>Pet Care</h3>
              <p>Loving care for your furry friends</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon-large">👴</div>
              <h3>Elderly Care</h3>
              <p>Compassionate care for seniors</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon-large">✂️</div>
              <h3>Hair Dresser</h3>
              <p>Professional haircuts and styling at home</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Choose Service</h3>
              <p>Select the type of help you need</p>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <h3>Find Helper</h3>
              <p>Browse qualified helpers in your area</p>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <h3>Book & Pay</h3>
              <p>Schedule your service and pay securely</p>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <h3>Get Help</h3>
              <p>Enjoy professional service at your doorstep</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Get Started?</h2>
              <p>Join thousands of satisfied customers who trust Sahayak for their home services</p>
              <div className="cta-buttons">
                <Link to="/register?role=customer" className="cta-button primary">
                  Book a Helper
                </Link>
                <Link to="/register?role=helper" className="cta-button secondary">
                  Become a Helper
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Sahayak</h3>
              <p>Connecting you with trusted home helpers for all your household needs.</p>
              <div className="footer-social">
                <a href="#" aria-label="Facebook">📘</a>
                <a href="#" aria-label="Instagram">📸</a>
                <a href="#" aria-label="Twitter">🐦</a>
                <a href="#" aria-label="LinkedIn">💼</a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/register?role=customer">Book a Helper</Link></li>
                <li><Link to="/register?role=helper">Become a Helper</Link></li>
                <li><Link to="/login">Login</Link></li>
                <li><a href="#services">Services</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li><Link to="/contact">Contact Us</Link></li>
                <li><a href="mailto:sahayak.platform@gmail.com">sahayak.platform@gmail.com</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions">Terms & Conditions</Link></li>
                <li><Link to="/cancellation-refund">Cancellation & Refund</Link></li>
                <li><Link to="/service-delivery">Service Delivery</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-divider"></div>
            <div className="footer-bottom-content">
              <p>&copy; 2025 Sahayak Technologies Pvt. Ltd. All rights reserved.</p>
              <div className="footer-badges">
                <span className="badge">🔒 Secure</span>
                <span className="badge">✅ Verified</span>
                <span className="badge">🏆 Trusted</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 